import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { FormattedMessage, injectIntl } from 'react-intl';
import { toastr } from 'react-redux-toastr';
import { RefreshCw, Percent, Clock, Check, Loader } from 'lucide-react';

// Style
import cx from 'classnames';
import withStyles from 'isomorphic-style-loader/lib/withStyles';
import s from './CurrencyManagement.css';
import CommonTable from '../../CommonTable/CommonTable';

// Redux Actions
import { updateCurrencyStatus, setBaseCurrency } from '../../../actions/siteadmin/CurrencyManagement/currencyManagement';
import { managePaymentCurrency } from '../../../actions/siteadmin/CurrencyManagement/managePaymentCurrency';

// Translation
import messages from '../../../locale/messages';

class CurrencyManagement extends React.Component {

  static propTypes = {
    data: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.number.isRequired,
      symbol: PropTypes.string.isRequired,
      isEnable: PropTypes.bool.isRequired,
      isPayment: PropTypes.bool.isRequired,
      isBaseCurrency: PropTypes.bool.isRequired
    })),
    updateCurrencyStatus: PropTypes.any.isRequired,
    setBaseCurrency: PropTypes.any.isRequired,
    managePaymentCurrency: PropTypes.any.isRequired,
    title: PropTypes.string.isRequired,
  };

  static defaultProps = {
    data: []
  };

  state = {
    isRefreshing: false,
    isSavingMarkup: false,
    markup: 2,
    lastUpdate: null,
    ratesSource: null
  };

  componentDidMount() {
    this.fetchFxSettings();
  }

  fetchFxSettings = async () => {
    try {
      const response = await fetch('/admin/fx-settings', {
        method: 'GET',
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        this.setState({
          markup: data.markup || 2,
          lastUpdate: data.lastUpdate,
          ratesSource: data.source
        });
      }
    } catch (error) {
      console.error('Error fetching FX settings:', error);
    }
  };

  handleRefreshRates = async () => {
    this.setState({ isRefreshing: true });
    try {
      const response = await fetch('/admin/update-currency-rates', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      });
      const data = await response.json();
      
      if (data.success) {
        toastr.success('Éxito', `Tasas actualizadas: ${data.data.ratesCount} monedas desde ${data.data.source}`);
        this.setState({
          lastUpdate: data.data.timestamp,
          ratesSource: data.data.source
        });
        // Reload page to show updated rates
        setTimeout(() => window.location.reload(), 1500);
      } else {
        toastr.error('Error', data.error || 'Error al actualizar tasas');
      }
    } catch (error) {
      toastr.error('Error', 'Error de conexión al actualizar tasas');
    } finally {
      this.setState({ isRefreshing: false });
    }
  };

  handleMarkupChange = (e) => {
    const value = parseFloat(e.target.value);
    if (!isNaN(value) && value >= 0 && value <= 20) {
      this.setState({ markup: value });
    }
  };

  handleSaveMarkup = async () => {
    const { markup } = this.state;
    this.setState({ isSavingMarkup: true });
    try {
      const response = await fetch('/admin/fx-settings', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ markup })
      });
      const data = await response.json();
      
      if (data.success) {
        toastr.success('Éxito', `Markup actualizado a ${markup}%`);
      } else {
        toastr.error('Error', data.error || 'Error al guardar markup');
      }
    } catch (error) {
      toastr.error('Error', 'Error de conexión al guardar markup');
    } finally {
      this.setState({ isSavingMarkup: false });
    }
  };

  handleUpdateStatus(id, status) {
    const { updateCurrencyStatus } = this.props;
    updateCurrencyStatus(id, status);
  }

  handleBaseCurrency(id) {
    const { setBaseCurrency } = this.props;
    setBaseCurrency(id);
  }

  thead = () => {
    const { formatMessage } = this.props.intl;
    return [
      { data: formatMessage(messages.idLabel) },
      { data: formatMessage(messages.symbolLabel) },
      { data: formatMessage(messages.baseCurrencyLabel) },
      { data: formatMessage(messages.status) },
      { data: formatMessage(messages.setEnableDisable) },
      { data: formatMessage(messages.setCurrencyLabel) },
      { data: formatMessage(messages.allowedPaymentCurrency) },
    ]
  };

  tbody = () => {
    const { data, managePaymentCurrency } = this.props;
    const { formatMessage } = this.props.intl;
    return data.map(value => {
      return {
        id: value?.id,
        data: [
          { data: value?.id },
          {
            data: value.symbol
          },
          {
            data: value.isBaseCurrency === true ? formatMessage(messages.activeLabel) : ""
          },
          {
            data: <span> {value.isEnable ? formatMessage(messages.enabledLabel) : formatMessage(messages.disabledLabel)} </span>
          },
          {
            data: <a href="javascript:void(0)" onClick={() => this.handleUpdateStatus(value.id, value.isEnable)} > {value.isEnable ? formatMessage(messages.disableLabel) : formatMessage(messages.enableLabel)} </a>
          },
          {
            data: <span>
              {
                !value.isBaseCurrency && value.isEnable && <a href="javascript:void(0)" onClick={() => this.handleBaseCurrency(value.id)}>
                  <FormattedMessage {...messages.setBaseCurrency} />
                </a>
              }
            </span>
          },
          {
            data: <span>
              {
                value.isEnable && <a href="javascript:void(0)" onClick={() => managePaymentCurrency(value.id, !value.isPayment ? "add" : "remove")}>
                  {!value.isPayment ? <FormattedMessage {...messages.addLabel} /> : <FormattedMessage {...messages.remove} />}
                </a>
              }
            </span>
          },
        ]
      }
    })
  }

  renderFxPanel = () => {
    const { isRefreshing, isSavingMarkup, markup, lastUpdate, ratesSource } = this.state;

    return (
      <div className={s.fxPanel}>
        <div className={s.fxHeader}>
          <h3 className={s.fxTitle}>Stripe Adaptive Pricing</h3>
          <span className={s.fxSubtitle}>Foreign Exchange Settings</span>
        </div>
        
        <div className={s.fxContent}>
          <div className={s.fxSection}>
            <div className={s.fxSectionHeader}>
              <RefreshCw size={16} className={s.fxSectionIcon} />
              <span>Exchange Rates</span>
            </div>
            <div className={s.fxSectionBody}>
              <p className={s.fxDescription}>
                Rates update automatically every 24 hours from Coinbase API.
              </p>
              {lastUpdate && (
                <div className={s.fxInfo}>
                  <Clock size={14} />
                  <span>{new Date(lastUpdate).toLocaleString()}</span>
                  {ratesSource && <span className={s.fxBadge}>{ratesSource}</span>}
                </div>
              )}
              <button
                className={s.fxButton}
                onClick={this.handleRefreshRates}
                disabled={isRefreshing}
              >
                {isRefreshing ? (
                  <><Loader size={14} className={s.fxSpinnerIcon} /> Updating...</>
                ) : (
                  <><RefreshCw size={14} /> Update Rates</>
                )}
              </button>
            </div>
          </div>

          <div className={s.fxSection}>
            <div className={s.fxSectionHeader}>
              <Percent size={16} className={s.fxSectionIcon} />
              <span>Protection Markup</span>
            </div>
            <div className={s.fxSectionBody}>
              <p className={s.fxDescription}>
                Added to exchange rates to protect against intraday fluctuations.
              </p>
              <div className={s.fxInputGroup}>
                <div className={s.fxInputWrapper}>
                  <input
                    type="number"
                    className={s.fxInput}
                    value={markup}
                    onChange={this.handleMarkupChange}
                    min="0"
                    max="20"
                    step="0.5"
                  />
                  <span className={s.fxInputSuffix}>%</span>
                </div>
                <button
                  className={cx(s.fxButton, s.fxButtonSave)}
                  onClick={this.handleSaveMarkup}
                  disabled={isSavingMarkup}
                >
                  {isSavingMarkup ? <Loader size={14} className={s.fxSpinnerIcon} /> : <Check size={14} />}
                  {isSavingMarkup ? ' Saving...' : ' Save'}
                </button>
              </div>
              <div className={s.fxExample}>
                Example: EUR 0.85 × 1.0{markup} = {(0.85 * (1 + markup/100)).toFixed(4)}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  render() {
    const { formatMessage } = this.props.intl;
    return (
      <div className={cx(s.pagecontentWrapper, 'pagecontentWrapperRTL')}>
        {this.renderFxPanel()}
        <CommonTable
          thead={this.thead}
          tbody={this.tbody}
          title={formatMessage(messages.manageCurrency)}
        />
      </div>
    );
  }
}

const mapState = (state) => ({});

const mapDispatch = {
  updateCurrencyStatus,
  setBaseCurrency,
  managePaymentCurrency
};

export default injectIntl(withStyles(s)(connect(mapState, mapDispatch)(CurrencyManagement)));